/**
 * Odczyt parametrów callbacku auth z URL-a.
 *
 * Musi być wykonany ZANIM powstanie klient supabase-js: po udanej wymianie
 * kodu PKCE biblioteka usuwa `code` przez history.replaceState, a przy flow
 * implicit czyści cały hash. Bez wcześniejszego snapshotu strona resetu nie
 * odróżni „nie było linku" od „link był, ale wymiana się nie powiodła".
 */

export type AuthCallbackParams = {
  /** Czy w URL-u był kod autoryzacyjny (sama wartość celowo nie jest przechowywana). */
  hasCode: boolean
  error: string | null
  errorCode: string | null
  errorDescription: string | null
}

const EMPTY: AuthCallbackParams = {
  hasCode: false,
  error: null,
  errorCode: null,
  errorDescription: null,
}

export function readAuthCallbackParams(href: string): AuthCallbackParams {
  let url: URL
  try {
    url = new URL(href)
  } catch {
    return EMPTY
  }

  // Supabase używa query dla PKCE i fragmentu dla implicit — czytamy oba.
  const search = url.searchParams
  const hash = new URLSearchParams(url.hash.startsWith('#') ? url.hash.slice(1) : url.hash)
  const pick = (key: string) => search.get(key) ?? hash.get(key)

  return {
    hasCode: Boolean(pick('code')),
    error: pick('error'),
    errorCode: pick('error_code'),
    errorDescription: pick('error_description'),
  }
}

/**
 * Komunikat dla użytkownika, który trafił na /auth/update-password bez ważnej
 * sesji resetu. Celowo nie zdradza, czy dany adres e-mail istnieje w bazie.
 */
export function describeRecoveryFailure(params: AuthCallbackParams): string {
  const haystack = `${params.error ?? ''} ${params.errorCode ?? ''} ${params.errorDescription ?? ''}`.toLowerCase()

  if (haystack.includes('expired')) {
    return 'Link do zmiany hasła wygasł. Poproś o nowy — będzie ważny przez godzinę.'
  }
  if (haystack.includes('already') || haystack.includes('used')) {
    return 'Ten link został już wykorzystany. Poproś o nowy, żeby ustawić hasło.'
  }
  if (haystack.trim().length > 0) {
    return 'Link do zmiany hasła jest nieprawidłowy lub stracił ważność. Poproś o nowy.'
  }

  // Brak błędu w URL-u, a mimo to brak sesji: najczęściej brakuje w tej
  // przeglądarce weryfikatora PKCE zapisanego przy wysyłce linku.
  return 'Nie udało się otworzyć sesji zmiany hasła. Link musi zostać otwarty w tej samej przeglądarce, w której prosiłeś o reset — mógł też wygasnąć lub zostać już użyty. Poproś o nowy link.'
}
